import * as moment from "moment";
import * as storeTypes from "./storeTypes";
import { combineReducers } from "redux";
import IUserState = storeTypes.IUserState;
import { IEventListItem, EventListItem } from "../event/eventListItem";
import { IMessageListItem, MessageListItem } from "../message/messageListItem";
import { ITaskListItem, TaskListItem } from "../task/taskListItem";
import { InstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { CalendarTaskItems } from "ui/Scripts/source/models/calendarTask/calendarTaskItems";
import { Base } from "ui/Scripts/source/framework/base";

export const initialState: storeTypes.IApplicationState = {
    //Fetch
    fetchCount: 0,
    //Install
    install: {
        proposalEvent: null
    },
    //Update
    update: {
        show: false,
        onYes: null,
        onNo: null
    },
    //Alert
    alert: {
        message: null,
        info: null,
        error: null
    },
    //Confirmation
    confirmation: {
        type: storeTypes.ConfirmationDialogType.Warning,
        title: null,
        text: null,
        show: false,
        onYes: null,
        onNo: null,
        onCancel: null,
    },
    //User
    user: {
        initialized: false,
        companyId: "",
        companyName: "",
        userCode: "",
        userName: "",
        userPicture: "",
        authenticated: false,
        canUseMessages: false,
        canUseDocuments: false,
        canUseEvents: false,
        canUseTasks: false,
        canUseInstantMessages: false,
        canEditOwnRecord: false,
        canUseCalendarTasks: false,
        canUseMultipleCompanies: false
    },
    //Front
    front: {
        general: {
            isLoading: false,
        },
        message: {
            isLoading: false,
            messages: []
        },
        event: {
            isLoading: false,
            events: []
        },
        task: {
            isLoading: false,
            canCreateTasks: false,
            tasks: []
        },
        document: {
            isLoading: false,
            documents: []
        },
        instantMessage: {
            isLoading: false,
            channels: []
        },
        calendarTask: {
            isLoading: false,
            calendarTasks: []
        }
    },
    //Message
    message: {
        messageList: {
            isLoading: false,
            hasMore: false,
            page: 0,
            items: [],
            companies: [],
            companyId: null,
            startDate: null,
            endDate: null,
            sortColumn: "",
            lastOpenedMessageId: ""
        },
        messageEdit: {
            isLoading: false,
            item: null,
            attachments: [],
            returnUrl: ""
        }
    },
    //Event
    event: {
        eventList: {
            isLoading: false,
            hasMore: false,
            page: 0,
            items: [],
            state: 0,
            startDate: null,
            endDate: null,
            unitId: null,
            units: [],
            sortColumn: "",
            lastOpenedEventId: ""
        },
        eventEdit: {
            isLoading: false,
            item: null,
            eventTypeNodes: [],
            eventTypeBNodes: [],
            eventTypeCNodes: [],
            attachments: [],
            createCompany: null,
            companies: [],
            units: [],
            urgencyClasses: [],
            eventSetting: null,
            userCompanyId: "",
            userCode: "",
            userName: "",
            userPicture: "",
            canHandleEvent: false,
            canRemoveEvent: false,
            canSaveEvent: false,
            canEditIdentificationData: false,
            userIsIsSystemAdmin: false,
            returnUrl: ""
        }
    },
    //Task
    task: {
        taskList: {
            isLoading: false,
            hasMore: false,
            page: 0,
            items: [],
            stateId: null,
            unitId: null,
            units: [],
            taskStates: [],
            sortColumn: "",
            lastOpenedTaskId: "",
            canCreateTasks: false
        },
        taskEdit: {
            isLoading: false,
            item: null,
            taskTypes: [],
            taskStates: [],
            taskPriorities: [],
            units: [],
            userCode: "",
            userName: "",
            userPicture: "",
            canHandleTask: false,
            canRemoveTask: false,
            canChangeTaskState: false,
            userIsIsSystemAdmin: false,
            returnUrl: ""
        }
    },
    //Document
    document: {
        documentList: {
            isLoading: false,
            hasMore: false,
            page: 0,
            companyId: "",
            parent: null,
            items: [],
            favoriteIds: [],
            companies: [],
            filter: "",
            sortColumn: "",
            sortOrderIsAsc: false,
            lastOpenedDocumentId: ""
        },
        documentView: {
            isLoading: false,
            item: null,
            returnUrl: ""
        }
    },
    //UserFurtherEducation
    userFurtherEducation: {
        userFurtherEducationList: {
            isLoading: false,
            hasMore: false,
            page: 0,
            archived: false,
            filter: "",
            items: [],
            qualifications: [],
            sortColumn: "state",
            sortOrderIsAsc: false,
            lastOpenedUserFurtherEducationId: ""
        },
        userFurtherEducationEdit: {
            isLoading: false,
            item: null,
            attachments: [],
            furtherEducationGroups: [],
            qualifications: [],
            returnUrl: ""
        }
    },
    //InstantMessage
    instantMessage: {
        instantMessageChannelList: {
            isLoading: false,
            items: [],
            lastOpenedInstantMessageChannelId: ""
        },
        instantMessageChannelEdit: {
            isLoading: false,
            item: null,
            returnUrl: ""
        },
        instantMessageChannel: {
            returnUrl: ""
        }
    },
    //CalendarTask
    calendarTask: {
        calendarTaskView: {
            isLoading: false,
            items: null,
            filter: "",
            stateId: "-1",
            categoryId: Base.emptyGuid,
            lastOpenedCalendarTasklId: ""
        },
        calendarTaskEdit: {
            isLoading: false,
            item: null,
            returnUrl: ""
        }
    },
    //IdCardView
    idCard: {
        isLoading: false,
        idCardItem: null
    }
};

//Fetch
function fetchCount(state: number = 0, action: storeTypes.FetchAction) {
    switch (action.type) {
    case storeTypes.FETCH_START:
        return state + 1;
    case storeTypes.FETCH_END:
        return state - 1;
    }
    return state;
}

//Install
function install(state: storeTypes.IInstallState = { proposalEvent: null }, action: storeTypes.InstallAction) {
    switch (action.type) {
    case storeTypes.INSTALL_SET:
        return { ...state, proposalEvent: (action as storeTypes.IInstallSet).event };
    case storeTypes.INSTALL_CLEAR:
        return { ...state, proposalEvent: null };
    }
    return state;
}

//Update
function update(state: storeTypes.IUpdateState = { show: false, onYes: null, onNo: null }, action: storeTypes.UpdateAction) {
    switch (action.type) {
    case storeTypes.UPDATE_SET: {
        const update = (action as storeTypes.IUpdateSet);
        return { ...state, show: true, onYes: update.onYes, onNo: update.onNo };
    }
    case storeTypes.UPDATE_CLEAR:
        return { ...state, show: false };
    }
    return state;
}

//Alert
function alert(state: storeTypes.IAlertState = { message: "", info: "", error: "" }, action: storeTypes.AlertAction) {
    switch (action.type) {
    case storeTypes.ALERT_SUCCESS:
        return { ...state, message: (action as storeTypes.IAlertSuccess).message };
    case storeTypes.ALERT_INFO:
        return { ...state, info: (action as storeTypes.IAlertInfo).message };
    case storeTypes.ALERT_ERROR:
        return { ...state, error: (action as storeTypes.IAlertError).message };
    case storeTypes.ALERT_CLEAR:
        return { message: null, info: null, error: null };
    case storeTypes.ALERT_SUCCESS_CLEAR:
        return { ...state, message: null };
    case storeTypes.ALERT_INFO_CLEAR:
        return { ...state, info: null };
    }
    return state;
}

//Confirmation
function confirmation(state: storeTypes.IConfirmationState = { type: storeTypes.ConfirmationDialogType.Warning, title: "", text: "", show: false, onYes: null, onNo: null, onCancel: null }, action: storeTypes.ConfirmationAction) {
    switch (action.type) {
    case storeTypes.CONFIRMATION_SET: {
        const confirmation = (action as storeTypes.IConfirmationSet);
        return { ...state, type: confirmation.dialogType, title: confirmation.title, text: confirmation.text, show: confirmation.show, onYes: confirmation.onYes, onNo: confirmation.onNo, onCancel: confirmation.onCancel };
    }
    case storeTypes.CONFIRMATION_CLEAR:
        return { ...state, show: false };
    }
    return state;
}

//User
function user(state: IUserState = null, action: storeTypes.LoginAction) {
    switch (action.type) {
    case storeTypes.LOGIN_SUCCESS: {
        const loginSuccess = action as storeTypes.ILoginSuccess;
        return {
            ...state,
            initialized: true,
            authenticated: true,
            companyId: loginSuccess.companyId,
            companyName: loginSuccess.companyName,
            userCode: loginSuccess.userCode,
            userName: loginSuccess.userName,
            userPicture: loginSuccess.userPicture,
            canUseMessages: loginSuccess.canUseMessages,
            canUseDocuments: loginSuccess.canUseDocuments,
            canUseEvents: loginSuccess.canUseEvents,
            canUseTasks: loginSuccess.canUseTasks,
            canUseInstantMessages: loginSuccess.canUseInstantMessages,
            canEditOwnRecord: loginSuccess.canEditOwnRecord,
            canUseCalendarTasks: loginSuccess.canUseCalendarTasks,
            canUseMultipleCompanies: loginSuccess.canUseMultipleCompanies,
        };
    }
    case storeTypes.LOGIN_FAILURE:
        return state;
    case storeTypes.LOGOUT_SUCCESS:
        return { ...state, initialized: true, authenticated: false, userName: "", userPicture: "", canUseMessages: false, canUseDocuments: false, canUseEvents: false, canUseTasks: false, canEditOwnRecord: false };
    }
    return state;
}

//Front
function frontPageGeneral(state: storeTypes.IFrontPageGeneralState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_FAILURE:
    case storeTypes.FRONT_PAGE_SUCCESS:
        return { ...state, isLoading: false };
    }
    return state;
}
function frontPageMessage(state: storeTypes.IFrontPageMessageState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_MESSAGE_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_MESSAGE_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_MESSAGE_SUCCESS: {
        const frontPageMessageSuccess = action as storeTypes.IFrontPageMessageSuccess;
        const frontPageMessage = frontPageMessageSuccess.frontPageMessage;
        return { ...state, isLoading: false, messages: frontPageMessage.messages };
    }
    }
    return state;
}

function frontPageEvent(state: storeTypes.IFrontPageEventState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_EVENT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_EVENT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_EVENT_SUCCESS: {
        const frontPageEventSuccess = action as storeTypes.IFrontPageEventSuccess;
        const frontPageEvent = frontPageEventSuccess.frontPageEvent;
        return { ...state, isLoading: false, events: frontPageEvent.events };
    }
    }
    return state;
}

function frontPageTask(state: storeTypes.IFrontPageTaskState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_TASK_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_TASK_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_TASK_SUCCESS: {
        const frontPageTaskSuccess = action as storeTypes.IFrontPageTaskSuccess;
        const frontPageTask = frontPageTaskSuccess.frontPageTask;
        return { ...state, isLoading: false, tasks: frontPageTask.tasks, canCreateTasks: frontPageTask.canCreateTasks };
    }
    }
    return state;
}

function frontPageDocument(state: storeTypes.IFrontPageDocumentState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_DOCUMENT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_DOCUMENT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_DOCUMENT_SUCCESS: {
        const frontPageDocumentSuccess = action as storeTypes.IFrontPageDocumentSuccess;
        const frontPageDocument = frontPageDocumentSuccess.frontPageDocument;
        return { ...state, isLoading: false, documents: frontPageDocument.documents };
    }
    }
    return state;
}

function frontPageInstantMessage(state: storeTypes.IFrontPageInstantMessageState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_INSTANTMESSAGE_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_INSTANTMESSAGE_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_INSTANTMESSAGE_SUCCESS: {
        const frontPageInstantMessageSuccess = action as storeTypes.IFrontPageInstantMessageSuccess;
        const frontPageInstantMessage = frontPageInstantMessageSuccess.frontPageInstantMessage;
        InstantMessageChannelItem.sortInstantMessageListItems(frontPageInstantMessage.channels);
        if (frontPageInstantMessage.channels.length > 3) {
            frontPageInstantMessage.channels = frontPageInstantMessage.channels.slice(0, 3);
        }
        return { ...state, isLoading: false, channels: frontPageInstantMessage.channels };
    }
    }
    return state;
}

function frontPageCalendarTask(state: storeTypes.IFrontPageCalendarTaskState = null, action: storeTypes.FrontAction) {
    switch (action.type) {
    case storeTypes.FRONT_PAGE_CALENDARTASK_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.FRONT_PAGE_CALENDARTASK_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.FRONT_PAGE_CALENDARTASK_SUCCESS: {
        const frontPageCalendarTaskSuccess = action as storeTypes.IFrontPageCalendarTaskSuccess;
        const frontPageCalendarTask = frontPageCalendarTaskSuccess.frontPageCalendarTask;
        return { ...state, isLoading: false, calendarTasks: frontPageCalendarTask.calendarTasks };
    }
    }
    return state;
}
//Message
function getMessageListItemsWithTitles(items: IMessageListItem[], sortColumn: string): IMessageListItem[] {
    let result: IMessageListItem[] = [];
    if (sortColumn.equalIgnoreCase("Date") && items.length > 0) {
        let previousYear = -1;
        let previousMonth = -1;
        for (let i = 0; i < items.length; i++) {
            const date = new Date(items[i].date);
            const year = date.getFullYear();
            const month = date.getMonth();
            if (year !== previousYear || month !== previousMonth) {
                result.push(MessageListItem.getTitleMessageListItem(moment.default().month(month).locale(appConfig.culture).format("MMMM") + " " + year.toFixed(0)));
                previousYear = year;
                previousMonth = month;
            }
            result.push(items[i]);
        }
    } else if (sortColumn.equalIgnoreCase("State") && items.length > 0) {
        let previousState = -1;
        for (let i = 0; i < items.length; i++) {
            const state = items[i].getStateNumberListSection();
            if (state !== previousState) {
                result.push(MessageListItem.getTitleMessageListItem(items[i].getStateStrListSection()));
                previousState = state;
            }
            result.push(items[i]);
        }
    } else {
        result = items;
    }
    return result;
}

function messageList(state: storeTypes.IMessageListState = null, action: storeTypes.MessageAction) {
    switch (action.type) {
    case storeTypes.MESSAGE_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.MESSAGE_LIST_SUCCESS: {
        const messageListSuccess = action as storeTypes.IMessageListSuccess;
        const messageList = messageListSuccess.messageList;
        const oldIds = {};
        for (let j = 0; j < state.items.length; j++) {
            oldIds[state.items[j].id] = true;
        }
        const oldItems = state.items.filter(i => !i.isTitle);
        const newItems = messageList.items.filter(i => Object.prototype.hasOwnProperty.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
        return {
            ...state,
            isLoading: false,
            hasMore: messageList.hasMore,
            page: messageList.page,
            items: getMessageListItemsWithTitles([...oldItems, ...newItems], messageList.sortColumn),
            startDate: messageListSuccess.startDate,
            endDate: messageListSuccess.endDate,
            companyId: messageListSuccess.companyId,
            companies: messageList.companies,
            sortColumn: messageList.sortColumn
        };
    }
    case storeTypes.MESSAGE_LIST_REFRESH: {
        const messageListRefresh = action as storeTypes.IMessageListRefresh;
        const messageList2 = messageListRefresh.messageList;
        return {
            ...state,
            isLoading: false,
            hasMore: messageList2.hasMore,
            page: messageList2.page,
            items: getMessageListItemsWithTitles(messageList2.items.slice(0), messageList2.sortColumn),
            startDate: messageListRefresh.startDate,
            endDate: messageListRefresh.endDate,
            companyId: messageListRefresh.companyId,
            companies: messageList2.companies,
            sortColumn: messageList2.sortColumn
        };
    }
    case storeTypes.MESSAGE_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.MESSAGE_LIST_SETLASTOPENED: {
        const messageId2 = (action as storeTypes.IMessageListSetLastOpened).messageId;
        return { ...state, lastOpenedMessageId: messageId2 };
    }
    case storeTypes.MESSAGE_LIST_RESET:
        return { ...state, companyId: null, startDate: null, endDate: null, sortColumn: "" };
    }
    return state;
}

function messageEdit(state: storeTypes.IMessageEditState = null, action: storeTypes.MessageAction) {
    switch (action.type) {
    case storeTypes.MESSAGE_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.MESSAGE_EDIT_SUCCESS: {
        const messageEdit = (action as storeTypes.IMessageEditSuccess).messageEdit;
        return { ...state, isLoading: false, item: messageEdit.item, attachments: messageEdit.attachments };
    }
    case storeTypes.MESSAGE_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.MESSAGE_EDIT_RESET:
        return { ...state, isLoading: false, item: null, attachments: [], returnUrl: "" };
    case storeTypes.MESSAGE_EDIT_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.IMessageEditSetReturnUrl).returnUrl };
    }
    return state;
}

//Event
function getEventListItemsWithTitles(items: IEventListItem[], sortColumn: string): IEventListItem[] {
    let result: IEventListItem[] = [];
    if (sortColumn.equalIgnoreCase("Date") && items.length > 0) {
        let previousYear = -1;
        let previousMonth = -1;
        for (let i = 0; i < items.length; i++) {
            const date = new Date(items[i].date);
            const year = date.getFullYear();
            const month = date.getMonth();
            if (year !== previousYear || month !== previousMonth) {
                result.push(EventListItem.getTitleEventListItem(moment.default().month(month).locale(appConfig.culture).format("MMMM") + " " + year.toFixed(0), ""));
                previousYear = year;
                previousMonth = month;
            }
            result.push(items[i]);
        }
    } else if (sortColumn.equalIgnoreCase("State") && items.length > 0) {
        let previousState = -1;
        for (let i = 0; i < items.length; i++) {
            if (items[i].state !== previousState) {
                result.push(EventListItem.getTitleEventListItem(items[i].pluralStateStr, "s" + items[i].state.toLocaleFixed(0)));
                previousState = items[i].state;
            }
            result.push(items[i]);
        }
    } else {
        result = items;
    }
    return result;
}

function eventList(state: storeTypes.IEventListState = null, action: storeTypes.EventAction) {
    switch (action.type) {
    case storeTypes.EVENT_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.EVENT_LIST_SUCCESS: {
        const eventListSuccess = action as storeTypes.IEventListSuccess;
        const eventList = eventListSuccess.eventList;
        const oldIds = {};
        for (let j = 0; j < state.items.length; j++) {
            oldIds[state.items[j].id] = true;
        }
        const oldItems = state.items.filter(i => !i.isTitle);
        const newItems = eventList.items.filter(i => Object.prototype.hasOwnProperty.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
        //[ ...state.items, ...newItems]
        return {
            ...state,
            isLoading: false,
            hasMore: eventList.hasMore,
            page: eventList.page,
            items: getEventListItemsWithTitles([...oldItems, ...newItems], eventList.sortColumn),
            state: eventListSuccess.state,
            startDate: eventListSuccess.startDate,
            endDate: eventListSuccess.endDate,
            unitId: eventListSuccess.unitId,
            units: eventList.units,
            sortColumn: eventList.sortColumn
        };
    }
    case storeTypes.EVENT_LIST_REFRESH: {
        const eventListRefresh = action as storeTypes.IEventListRefresh;
        const eventList2 = eventListRefresh.eventList;
        return {
            ...state,
            isLoading: false,
            hasMore: eventList2.hasMore,
            page: eventList2.page,
            items: getEventListItemsWithTitles(eventList2.items.slice(0), eventList2.sortColumn),
            state: eventListRefresh.state,
            startDate: eventListRefresh.startDate,
            endDate: eventListRefresh.endDate,
            unitId: eventListRefresh.unitId,
            units: eventList2.units,
            sortColumn: eventList2.sortColumn
        };
    }
    case storeTypes.EVENT_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.EVENT_LIST_REMOVE: {
        const eventId = (action as storeTypes.IEventListRemove).eventId;
        return { ...state, isLoading: false, items: state.items.filter(i => i.id !== eventId) };
    }
    case storeTypes.EVENT_LIST_RESET:
        return { ...state, state: 0, startDate: null, endDate: null, unitId: null, units: [], sortColumn: "" };
    case storeTypes.EVENT_LIST_SETLASTOPENED: {
        const eventId2 = (action as storeTypes.IEventListSetLastOpened).eventId;
        return { ...state, lastOpenedEventId: eventId2 };
    }
    case storeTypes.EVENT_LIST_SETLOCKED: {
        const eventListSetLocked = action as storeTypes.IEventListSetLocked;
        const items = state.items.slice(0);
        const index = items.findIndex(i => i.id === eventListSetLocked.eventId);
        if (index < 0) {
            return state;
        }
        items[index].locked = eventListSetLocked.locked;
        return { ...state, items };
    }
    }
    return state;
}

function eventEdit(state: storeTypes.IEventEditState = null, action: storeTypes.EventAction) {
    switch (action.type) {
    case storeTypes.EVENT_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.EVENT_EDIT_SUCCESS: {
        const eventEdit = (action as storeTypes.IEventEditSuccess).eventEdit;
        return {
            ...state,
            isLoading: false,
            item: eventEdit.item,
            eventTypeNodes: eventEdit.eventTypeNodes,
            eventTypeBNodes: eventEdit.eventTypeBNodes,
            eventTypeCNodes: eventEdit.eventTypeCNodes,
            attachments: eventEdit.attachments,
            createCompany: eventEdit.createCompany,
            companies: eventEdit.companies,
            units: eventEdit.units,
            urgencyClasses: eventEdit.urgencyClasses,
            eventSetting: eventEdit.eventSetting,
            canHandleEvent: eventEdit.canHandleEvent,
            canSaveEvent: eventEdit.canSaveEvent,
            canRemoveEvent: eventEdit.canRemoveEvent,
            canEditIdentificationData: eventEdit.canEditIdentificationData,
            userIsIsSystemAdmin: eventEdit.userIsIsSystemAdmin,
            userCompanyId: eventEdit.userCompanyId,
            userCode: eventEdit.userCode,
            userName: eventEdit.userName,
            userPicture: eventEdit.userPicture
        };
    }
    case storeTypes.EVENT_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.EVENT_EDIT_RESET:
        return {
            ...state,
            isLoading: false,
            item: null,
            eventTypes: [],
            eventTypeBs: [],
            eventTypeCs: [],
            attachments: [],
            createCompany: null,
            companies: [],
            units: [],
            urgencyClasses: [],
            eventSetting: null,
            canHandleEvent: false,
            canRemoveEvent: false,
            canSaveEvent: false,
            canEditIdentificationData: false,
            userIsIsSystemAdmin: false,
            userCompanyId: "",
            userCode: "",
            userName: "",
            userPicture: "",
            returnUrl: ""
        };
    case storeTypes.EVENT_EDIT_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.IEventEditSetReturnUrl).returnUrl };
    }
    return state;
}

//Task
function getTaskListItemsWithTitles(items: ITaskListItem[], sortColumn: string): ITaskListItem[] {
    let result: ITaskListItem[] = [];
    if (sortColumn.equalIgnoreCase("ModifyDate") && items.length > 0) {
        let previousYear = -1;
        let previousMonth = -1;
        for (let i = 0; i < items.length; i++) {
            const date = new Date(items[i].modifyDate);
            const year = date.getFullYear();
            const month = date.getMonth();
            if (year !== previousYear || month !== previousMonth) {
                result.push(TaskListItem.getTitleTaskListItem(moment.default().month(month).locale(appConfig.culture).format("MMMM") + " " + year.toFixed(0), ""));
                previousYear = year;
                previousMonth = month;
            }
            result.push(items[i]);
        }
    } else if (sortColumn.equalIgnoreCase("StateCode") && items.length > 0) {
        let previousStateCode = "";
        for (let i = 0; i < items.length; i++) {
            const stateCode = items[i].stateCode;
            if (stateCode !== previousStateCode) {
                result.push(TaskListItem.getTitleTaskListItem(stateCode, "s" + items[i].stateSortOrder.toLocaleFixed(0)));
                previousStateCode = stateCode;
            }
            result.push(items[i]);
        }
    } else {
        result = items;
    }
    return result;
}

function taskList(state: storeTypes.ITaskListState = null, action: storeTypes.TaskAction) {
    switch (action.type) {
    case storeTypes.TASK_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.TASK_LIST_SUCCESS: {
        const taskListSuccess = action as storeTypes.ITaskListSuccess;
        const taskList = taskListSuccess.taskList;
        const oldIds = {};
        for (let j = 0; j < state.items.length; j++) {
            oldIds[state.items[j].id] = true;
        }
        const oldItems = state.items.filter(i => !i.isTitle);
        const newItems = taskList.items.filter(i => Object.prototype.hasOwnProperty.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
        //[ ...state.items, ...newItems]
        return {
            ...state,
            isLoading: false,
            hasMore: taskList.hasMore,
            page: taskList.page,
            items: getTaskListItemsWithTitles([...oldItems, ...newItems], taskList.sortColumn),
            stateId: taskListSuccess.stateId,
            unitId: taskListSuccess.unitId,
            units: taskList.units,
            taskStates: taskList.taskStates,
            sortColumn: taskList.sortColumn,
            canCreateTasks: taskList.canCreateTasks
        };
    }
    case storeTypes.TASK_LIST_REFRESH: {
        const taskListRefresh = action as storeTypes.ITaskListRefresh;
        const taskList2 = taskListRefresh.taskList;
        return {
            ...state,
            isLoading: false,
            hasMore: taskList2.hasMore,
            page: taskList2.page,
            items: getTaskListItemsWithTitles(taskList2.items.slice(0), taskList2.sortColumn),
            stateId: taskListRefresh.stateId,
            unitId: taskListRefresh.unitId,
            units: taskList2.units,
            taskStates: taskList2.taskStates,
            sortColumn: taskList2.sortColumn,
            canCreateTasks: taskList2.canCreateTasks
        };
    }
    case storeTypes.TASK_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.TASK_LIST_REMOVE: {
        const taskId = (action as storeTypes.ITaskListRemove).taskId;
        return { ...state, isLoading: false, items: state.items.filter(i => i.id !== taskId) };
    }
    case storeTypes.TASK_LIST_RESET:
        return { ...state, state: 0, startDate: null, endDate: null, unitId: null, units: [], sortColumn: "" };
    case storeTypes.TASK_LIST_SETLASTOPENED: {
        const taskId2 = (action as storeTypes.ITaskListSetLastOpened).taskId;
        return { ...state, lastOpenedTaskId: taskId2 };
    }
    case storeTypes.TASK_LIST_SETLOCKED: {
        const taskListSetLocked = action as storeTypes.ITaskListSetLocked;
        const items = state.items.slice(0);
        const index = items.findIndex(i => i.id === taskListSetLocked.taskId);
        if (index < 0) {
            return state;
        }
        items[index].locked = taskListSetLocked.locked;
        return { ...state, items };
    }
    }
    return state;
}

function taskEdit(state: storeTypes.ITaskEditState = null, action: storeTypes.TaskAction) {
    switch (action.type) {
    case storeTypes.TASK_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.TASK_EDIT_SUCCESS: {
        const taskEdit = (action as storeTypes.ITaskEditSuccess).taskEdit;
        return {
            ...state,
            isLoading: false,
            item: taskEdit.item,
            taskTypes: taskEdit.taskTypes,
            taskStates: taskEdit.taskStates,
            taskPriorities: taskEdit.taskPriorities,
            units: taskEdit.units,
            canHandleTask: taskEdit.canHandleTask,
            canRemoveTask: taskEdit.canRemoveTask,
            canChangeTaskState: taskEdit.canChangeTaskState,
            userIsIsSystemAdmin: taskEdit.userIsIsSystemAdmin,
            userCode: taskEdit.userCode,
            userName: taskEdit.userName,
            userPicture: taskEdit.userPicture
        };
    }
    case storeTypes.TASK_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.TASK_EDIT_RESET:
        return {
            ...state,
            isLoading: false,
            item: null,
            taskTypes: [],
            taskStates: [],
            taskPriorities: [],
            units: [],
            canHandleTask: false,
            canRemoveTask: false,
            canChangeTaskState: false,
            userIsIsSystemAdmin: false,
            userCode: "",
            userName: "",
            userPicture: "",
            returnUrl: ""
        };
    case storeTypes.TASK_EDIT_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.ITaskEditSetReturnUrl).returnUrl };
    }
    return state;
}

//Document
function documentList(state: storeTypes.IDocumentListState = null, action: storeTypes.DocumentAction) {
    switch (action.type) {
    case storeTypes.DOCUMENT_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.DOCUMENT_LIST_SUCCESS: {
        const documentListSuccess = action as storeTypes.IDocumentListSuccess;
        const documentList = documentListSuccess.documentList;
        return {
            ...state,
            isLoading: false,
            hasMore: documentList.hasMore,
            page: documentList.page,
            companyId: documentList.companyId,
            parent: documentList.parent,
            items: documentList.items.slice(0),
            favoriteIds: documentList.favoriteIds.slice(0),
            companies: documentList.companies,
            filter: documentListSuccess.filter,
            sortColumn: documentListSuccess.documentList.sortColumn,
            sortOrderIsAsc: documentListSuccess.documentList.sortOrderIsAsc
        };
    }
    case storeTypes.DOCUMENT_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.DOCUMENT_LIST_SETLASTOPENED: {
        const documentId2 = (action as storeTypes.IDocumentListSetLastOpened).documentId;
        return { ...state, lastOpenedDocumentId: documentId2 };
    }
    case storeTypes.DOCUMENT_LIST_RESET:
        return { ...state, filter: "", sortColumn: "", sortOrderIsAsc: false };
    case storeTypes.DOCUMENT_LIST_ADD_FAVORITE: {
        const addFolderId = (action as storeTypes.IDocumentListAddFavorite).folderId;
        const favoriteIds = state.favoriteIds.slice(0);
        favoriteIds.push(addFolderId);
        return { ...state, favoriteIds };
    }
    case storeTypes.DOCUMENT_LIST_REMOVE_FAVORITE: {
        const removeFolderId = (action as storeTypes.IDocumentListRemoveFavorite).folderId;
        return { ...state, favoriteIds: state.favoriteIds.filter(i => i !== removeFolderId) };
    }
    }
    return state;
}

function documentView(state: storeTypes.IDocumentViewState = null, action: storeTypes.DocumentAction) {
    switch (action.type) {
    case storeTypes.DOCUMENT_VIEW_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.DOCUMENT_VIEW_SUCCESS: {
        const documentView = (action as storeTypes.IDocumentViewSuccess).documentView;
        return {
            ...state,
            isLoading: false,
            item: documentView.item
        };
    }
    case storeTypes.DOCUMENT_VIEW_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.DOCUMENT_VIEW_RESET:
        return {
            ...state,
            isLoading: false,
            item: null
        };
    case storeTypes.DOCUMENT_VIEW_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.IDocumentViewSetReturnUrl).returnUrl };
    }
    return state;
}

//UserFurtherEducation
function userFurtherEducationList(state: storeTypes.IUserFurtherEducationListState = null, action: storeTypes.UserFurtherEducationAction) {
    switch (action.type) {
    case storeTypes.USERFURTHEREDUCATION_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.USERFURTHEREDUCATION_LIST_SUCCESS: {
        const listSuccess = action as storeTypes.IUserFurtherEducationListSuccess;
        const list = listSuccess.list;
        const oldIds = {};
        for (let j = 0; j < state.items.length; j++) {
            oldIds[state.items[j].id] = true;
        }
        const oldItems = state.items.slice(0);
        const newItems = list.items.filter(i => Object.prototype.hasOwnProperty.call(oldIds, i.id) ? false : (oldIds[i.id] = true));
        return {
            ...state,
            isLoading: false,
            hasMore: list.hasMore,
            page: list.page,
            archived: listSuccess.archived,
            items: [...oldItems, ...newItems],
            qualifications: list.qualifications,
            sortColumn: list.sortColumn
        };
    }
    case storeTypes.USERFURTHEREDUCATION_LIST_REFRESH: {
        const listRefresh = action as storeTypes.IUserFurtherEducationListRefresh;
        const list = listRefresh.list;
        return {
            ...state,
            isLoading: false,
            hasMore: list.hasMore,
            page: list.page,
            archived: listRefresh.archived,
            items: list.items.slice(0),
            qualifications: list.qualifications,
            sortColumn: list.sortColumn
        };
    }
    case storeTypes.USERFURTHEREDUCATION_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.USERFURTHEREDUCATION_LIST_REMOVE: {
        const userFurtherEducationId = (action as storeTypes.IUserFurtherEducationListRemove).userFurtherEducationId;
        return { ...state, isLoading: false, items: state.items.filter(i => i.id !== userFurtherEducationId) };
    }
    case storeTypes.USERFURTHEREDUCATION_LIST_RESET:
        return { ...state, state: 0, startDate: null, endDate: null, unitId: null, units: [], sortColumn: "" };
    case storeTypes.USERFURTHEREDUCATION_LIST_SETLASTOPENED: {
        const userFurtherEducationId2 = (action as storeTypes.IUserFurtherEducationListSetLastOpened).userFurtherEducationId;
        return { ...state, lastOpenedEventId: userFurtherEducationId2 };
    }
    }
    return state;
}

function userFurtherEducationEdit(state: storeTypes.IUserFurtherEducationEditState = null, action: storeTypes.UserFurtherEducationAction) {
    switch (action.type) {
    case storeTypes.USERFURTHEREDUCATION_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.USERFURTHEREDUCATION_EDIT_SUCCESS: {
        const edit = (action as storeTypes.IUserFurtherEducationEditSuccess).userFurtherEducationEdit;
        return {
            ...state,
            isLoading: false,
            item: edit.item,
            attachments: edit.attachments,
            furtherEducationGroups: edit.furtherEducationGroups,
            qualifications: edit.qualifications,
        };
    }
    case storeTypes.USERFURTHEREDUCATION_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.USERFURTHEREDUCATION_EDIT_RESET:
        return {
            ...state,
            isLoading: false,
            item: null,
            attachments: [],
            furtherEducationGroups: [],
            qualifications: [],
            returnUrl: ""
        };
    case storeTypes.USERFURTHEREDUCATION_EDIT_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.IUserFurtherEducationEditSetReturnUrl).returnUrl };
    }
    return state;
}

//InstantMessage
function instantMessageChannelList(state: storeTypes.IInstantMessageChannelListState = null, action: storeTypes.InstantMessageAction) {
    switch (action.type) {
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_SUCCESS: {
        const listSuccess = action as storeTypes.IInstantMessageChannelListSuccess;
        InstantMessageChannelItem.sortInstantMessageListItems(listSuccess.list.channels);
        return {
            ...state,
            isLoading: false,
            items: listSuccess.list.channels
        };
    }
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_REMOVE: {
        const id = (action as storeTypes.IInstantMessageChannelListRemove).instantMessageChannelId;
        return { ...state, isLoading: false, items: state.items.filter(i => i.id !== id) };
    }
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_RESET:
        return { ...state, lastOpenedInstantMessageChannelId: "" };
    case storeTypes.INSTANTMESSAGECHANNEL_LIST_SETLASTOPENED: {
        const id = (action as storeTypes.IInstantMessageChannelListSetLastOpened).instantMessageChannelId;
        return { ...state, lastOpenedInstantMessageChannelId: id };
    }
    }
    return state;
}

function instantMessageChannelEdit(state: storeTypes.IInstantMessageChannelEditState = null, action: storeTypes.InstantMessageAction) {
    switch (action.type) {
    case storeTypes.INSTANTMESSAGECHANNEL_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.INSTANTMESSAGECHANNEL_EDIT_SUCCESS: {
        const newSuccess = action as storeTypes.IInstantMessageChannelEditSuccess;
        return {
            ...state,
            isLoading: false,
            item: newSuccess.item
        };
    }
    case storeTypes.INSTANTMESSAGECHANNEL_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.INSTANTMESSAGECHANNEL_EDIT_RESET:
        return { ...state, item: null };
    case storeTypes.INSTANTMESSAGECHANNEL_EDIT_SETRETURNURL: {
        return { ...state, returnUrl: (action as storeTypes.IInstantMessageChannelEditSetReturnUrl).returnUrl };
    }
    }
    return state;
}

function instantMessageChannel(state: storeTypes.IInstantMessageChannelState = null, action: storeTypes.InstantMessageAction) {
    switch (action.type) {
    case storeTypes.INSTANTMESSAGECHANNEL_OPEN_SETRETURNURL:
        return { ...state, returnUrl: (action as storeTypes.IInstantMessageChannelOpenSetReturnUrl).returnUrl };
    }
    return state;
}

//CalendarTask
function calendarTaskView(state: storeTypes.ICalendarTaskViewState = null, action: storeTypes.CalendarTaskAction) {
    switch (action.type) {
    case storeTypes.CALENDARTASK_VIEW_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.CALENDARTASK_VIEW_SUCCESS: {
        const viewSuccess = action as storeTypes.ICalendarTaskViewSuccess;
        return {
            ...state,
            isLoading: false,
            items: viewSuccess.items,
            filter: viewSuccess.filter,
            stateId: viewSuccess.stateId,
            categoryId: viewSuccess.categoryId,
        };
    }
    case storeTypes.CALENDARTASK_VIEW_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.CALENDARTASK_VIEW_REMOVE: {
        const id = (action as storeTypes.ICalendarTaskViewRemove).calendarTaskId;
        const items = new CalendarTaskItems(state.items);
        items.items = state.items.items.filter(i => i.id !== id);
        return { ...state, isLoading: false, items };
    }
    case storeTypes.CALENDARTASK_VIEW_RESET:
        return { ...state, lastOpenedCalendarTasklId: "", filter: "" };
    case storeTypes.CALENDARTASK_VIEW_SETLASTOPENED: {
        const id = (action as storeTypes.ICalendarTaskViewSetLastOpened).calendarTaskId;
        return { ...state, lastOpenedCalendarTasklId: id };
    }
    case storeTypes.CALENDARTASK_VIEW_SETLOCKED: {
        const calendarTaskViewSetLocked = action as storeTypes.ICalendarTaskViewSetLocked;
        const items = new CalendarTaskItems(state.items);
        const index = items.items.findIndex(i => i.id === calendarTaskViewSetLocked.calendarTaskId);
        if (index < 0) {
            return state;
        }
        //items.items[index].locked = calendarTaskViewSetLocked.locked;
        return { ...state, items };
    }
    }
    return state;
}

function calendarTaskEdit(state: storeTypes.ICalendarTaskEditState = null, action: storeTypes.CalendarTaskAction) {
    switch (action.type) {
    case storeTypes.CALENDARTASK_EDIT_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.CALENDARTASK_EDIT_SUCCESS: {
        const editSuccess = action as storeTypes.ICalendarTaskEditSuccess;
        return {
            ...state,
            isLoading: false,
            item: editSuccess.item
        };
    }
    case storeTypes.CALENDARTASK_EDIT_FAILURE:
        return { ...state, isLoading: false };
    case storeTypes.CALENDARTASK_EDIT_RESET:
        return { ...state, item: null };
    case storeTypes.CALENDARTASK_EDIT_SETRETURNURL: {
        return { ...state, returnUrl: (action as storeTypes.ICalendarTaskEditSetReturnUrl).returnUrl };
    }
    }
    return state;
}

//IdCardView
function idCard(state: storeTypes.IUserIdCardState = null, action: storeTypes.IdCardViewAction) {
    switch (action.type) {
    case storeTypes.IDCARD_VIEW_REQUEST:
        return { ...state, isLoading: true };
    case storeTypes.IDCARD_VIEW_SUCCESS: {
        const viewSuccess = action as storeTypes.IIdCardViewSuccess;
        return {
            ...state,
            isLoading: false,
            idCardItem: viewSuccess.item
        };
    }
    case storeTypes.IDCARD_VIEW_FAILURE:
        return { ...state, isLoading: false };
    }
    return state;
}

//Root reducer
export const reducer = combineReducers({
    fetchCount,
    update,
    install,
    alert,
    confirmation,
    user,
    front:
        combineReducers({
            general: frontPageGeneral,
            message: frontPageMessage,
            event: frontPageEvent,
            task: frontPageTask,
            document: frontPageDocument,
            instantMessage: frontPageInstantMessage,
            calendarTask: frontPageCalendarTask
        }),
    message:
        combineReducers({
            messageList,
            messageEdit
        }),
    event:
        combineReducers({
            eventList,
            eventEdit
        }),
    task:
        combineReducers({
            taskList,
            taskEdit
        }),
    document:
        combineReducers({
            documentList,
            documentView
        }),
    userFurtherEducation:
        combineReducers({
            userFurtherEducationList,
            userFurtherEducationEdit
        }),
    instantMessage:
        combineReducers({
            instantMessageChannelList,
            instantMessageChannelEdit,
            instantMessageChannel
        }),
    calendarTask:
        combineReducers({
            calendarTaskView,
            calendarTaskEdit
        }),
    idCard
});

export default reducer;